if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      variantRequestToken = 0;
      variantTransactionCounter = 0;
      pendingRequestUrl = null;
      lastCommittedVariantState = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        queueMicrotask(() => {
          this.syncOptionControls(this.getSelectedOptionValues());
          if (!this.lastCommittedVariantState) {
            this.lastCommittedVariantState = this.captureCommittedVariantState();
            this.dataset.kkLastCommittedVariantId =
              this.lastCommittedVariantState.variantId || '';
          }
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.variantRequestToken += 1;
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.commitVariantSelection({
          optionValueIds: selectedOptionValues,
          options: this.getSelectedOptionValues(),
          origin: event.kkUpdateOrigin || 'option-picker',
          transactionId: event.kkTransactionId || this.createVariantTransactionId(),
          productUrl: target.dataset.productUrl || this.dataset.url,
          targetId: target.id,
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      createVariantTransactionId() {
        this.variantTransactionCounter += 1;
        return `kk-${this.dataset.section || 'product'}-${this.variantTransactionCounter}`;
      }

      getSelectedOptionValues() {
        return Array.from(this.variantSelectors?.querySelectorAll('.product-form__input') || []).map((group) => {
          const selected = group.querySelector('input[type="radio"]:checked, select');
          return selected?.value || null;
        });
      }

      syncOptionControls(options = []) {
        const groups = Array.from(this.variantSelectors?.querySelectorAll('.product-form__input') || []);
        groups.forEach((group, index) => {
          const value = options[index];
          if (value == null) return;

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const selected = input.value === value;
            input.checked = selected;
            input.setAttribute('aria-checked', selected ? 'true' : 'false');
            const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            if (!label) return;
            label.setAttribute('aria-selected', selected ? 'true' : 'false');
            if (selected) label.setAttribute('aria-current', 'true');
            else label.removeAttribute('aria-current');
          });

          const select = group.querySelector('select');
          if (select) {
            select.value = value;
            Array.from(select.options).forEach((option) => {
              option.toggleAttribute('selected', option.value === value);
            });
          }

          const selectedValue = group.querySelector('[data-selected-value]');
          if (selectedValue && !selectedValue.querySelector('.swatch')) selectedValue.textContent = value;
        });
      }

      commitVariantSelection({
        variantId = null,
        options = [],
        optionValueIds = [],
        origin = 'option-picker',
        transactionId = this.createVariantTransactionId(),
        productUrl = this.dataset.url,
        targetId = null,
      } = {}) {
        if (options.length) this.syncOptionControls(options);
        this.resetProductFormState();

        const finalProductUrl = productUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== finalProductUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const requestContext = {
          variantId: variantId ? String(variantId) : null,
          options: [...options],
          optionValueIds: [...optionValueIds],
          origin,
          transactionId,
          productUrl: finalProductUrl,
        };

        this.pendingRequestUrl = finalProductUrl;
        this.setAttribute('aria-busy', 'true');
        this.productForm?.submitButton?.setAttribute('aria-disabled', 'true');
        this.dispatchEvent(
          new CustomEvent('kk:variant-selection-pending', {
            bubbles: true,
            detail: requestContext,
          })
        );

        return this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            finalProductUrl,
            { optionValues: optionValueIds, variantId },
            shouldFetchFullPage
          ),
          targetId,
          requestContext,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(finalProductUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(finalProductUrl, {
                preserveOptionControls:
                  Boolean(variantId) || document.body.dataset.kkTemplate === 'hyttebok-premium',
                expectedVariantId: variantId,
                requestContext,
              }),
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
          return { status: 'applied', variant };
        };
      }

      async renderProductInfo({ requestUrl, targetId, callback, requestContext = {} }) {
        const requestToken = ++this.variantRequestToken;
        const requestedAt = Date.now();
        this.dataset.kkVariantRequestCount = String(
          Number(this.dataset.kkVariantRequestCount || 0) + 1
        );
        this.dataset.kkVariantRequestToken = String(requestToken);
        this.abortController?.abort();
        this.abortController = new AbortController();
        const context = { ...requestContext, requestToken, requestedAt };
        this.querySelector('media-gallery')?.noteVariantRequest?.(context);

        try {
          const response = await fetch(requestUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Variant request failed with status ${response.status}`);
          const responseText = await response.text();
          if (requestToken !== this.variantRequestToken) return { status: 'stale' };

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const result = (await callback(html, context)) || { status: 'applied' };
          if (requestToken !== this.variantRequestToken) return { status: 'stale' };
          if (result.status !== 'applied') {
            if (
              ['mismatch', 'error', 'media-error'].includes(result.status)
            ) {
              await this.restoreLastCommittedVariantState();
            }
            return result;
          }

          if (targetId) {
            const focusTarget = this.querySelector(`#${CSS.escape(targetId)}`);
            if (focusTarget) {
              const preserveScroll = [
                'bryllup-gjestebok',
                'dap-gjestebok',
                'konfirmasjon-gjestebok',
                'hyttebok-premium',
              ].includes(document.body.dataset.kkTemplate);
              focusTarget.focus(preserveScroll ? { preventScroll: true } : undefined);
            }
          }
          return result;
        } catch (error) {
          if (error.name === 'AbortError') return { status: 'stale' };
          if (requestToken === this.variantRequestToken) {
            await this.restoreLastCommittedVariantState();
          }
          console.error(error);
          return { status: 'error', error };
        } finally {
          if (requestToken === this.variantRequestToken) {
            this.pendingRequestUrl = null;
            this.removeAttribute('aria-busy');
            const submitButton = this.productForm?.submitButton;
            if (submitButton?.hasAttribute('disabled')) submitButton.setAttribute('aria-disabled', 'true');
            else submitButton?.removeAttribute('aria-disabled');
          }
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, { optionValues = [], variantId = null } = {}, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.search = '';
        if (!shouldFetchFullPage) requestUrl.searchParams.set('section_id', this.sectionId);
        if (variantId) requestUrl.searchParams.set('variant', variantId);
        else if (optionValues.length) requestUrl.searchParams.set('option_values', optionValues.join(','));
        return requestUrl.href;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(
        productUrl,
        { preserveOptionControls = false, expectedVariantId = null, requestContext = {} } = {}
      ) {
        return async (html, responseContext = requestContext) => {
          const variant = this.getSelectedVariant(html);
          if (expectedVariantId && (!variant || String(variant.id) !== String(expectedVariantId))) {
            return { status: 'mismatch', variant };
          }

          if (variant) {
            const mediaContext = {
              origin: 'variant',
              syncVariant: false,
              source: 'variant-response',
              transactionId: responseContext.transactionId,
              requestToken: responseContext.requestToken,
              variantId: String(variant.id),
              latestApprovedVariant:
                responseContext.requestToken === this.variantRequestToken,
            };
            let mediaResult = await this.updateMedia(
              html,
              variant.featured_media?.id,
              mediaContext
            );
            if (
              mediaResult.status === 'rejected-stale' &&
              responseContext.requestToken === this.variantRequestToken &&
              mediaResult.newerUserTransaction !== true
            ) {
              mediaResult = await this.updateMedia(html, variant.featured_media?.id, {
                ...mediaContext,
                latestApprovedVariant: true,
              });
            }
            this.dataset.kkVariantMediaStatus = mediaResult.status;
            this.dataset.kkVariantMediaId = mediaResult.mediaId || '';
            const mediaSupersededByNewerUser =
              mediaResult.status === 'rejected-stale' &&
              mediaResult.newerUserTransaction === true;
            if (
              !mediaSupersededByNewerUser &&
              variant.featured_media?.id &&
              !['applied', 'already-active'].includes(mediaResult.status)
            ) {
              return {
                status:
                  mediaResult.status === 'rejected-stale'
                    ? 'stale-media'
                    : 'media-error',
                variant,
                mediaResult,
              };
            }
          }

          this.pickupAvailability?.update(variant);
          if (preserveOptionControls) {
            if (variant?.options) this.syncOptionControls(variant.options);
            const selectedVariantData = this.variantSelectors?.querySelector('[data-selected-variant]');
            if (selectedVariantData) selectedVariantData.textContent = variant ? JSON.stringify(variant) : '';
          } else {
            this.updateOptionValues(html);
            if (variant?.options) this.syncOptionControls(variant.options);
          }
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.lastCommittedVariantState = this.captureCommittedVariantState({
              requestToken: responseContext.requestToken,
              transactionId: responseContext.transactionId,
              origin: responseContext.origin,
              available: false,
            });
            return { status: 'applied', variant: null };
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(variant.available === false, window.variantStrings.soldOut);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
              origin: responseContext.origin,
              transactionId: responseContext.transactionId,
              requestToken: responseContext.requestToken,
            },
          });
          this.lastCommittedVariantState = this.captureCommittedVariantState({
            requestToken: responseContext.requestToken,
            transactionId: responseContext.transactionId,
            origin: responseContext.origin,
          });
          this.dataset.kkLastCommittedVariantId = String(variant.id);
          this.dispatchEvent(
            new CustomEvent('kk:variant-selection-committed', {
              bubbles: true,
              detail: this.lastCommittedVariantState,
            })
          );
          return { status: 'applied', variant };
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const nextUrl = new URL(url, window.location.origin);
        nextUrl.search = '';
        if (variantId) nextUrl.searchParams.set('variant', variantId);
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${nextUrl.pathname}${nextUrl.search}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      captureCommittedVariantState(overrides = {}) {
        const variant = this.getSelectedVariant(this);
        const mediaGallery = this.querySelector('media-gallery');
        const featuredMediaId = String(
          mediaGallery?.querySelector('[id^="GalleryViewer"] [data-media-id].is-active')?.dataset.mediaId || ''
        )
          .split('-')
          .pop();
        const submitButton = this.productForm?.submitButton;
        const price = this.querySelector(`#price-${this.dataset.section}`);

        return {
          variantId: variant?.id ? String(variant.id) : this.productForm?.variantIdInput?.value || null,
          size: this.getSelectedOptionValues()[0] || null,
          color: this.getSelectedOptionValues()[1] || null,
          motif: this.getSelectedOptionValues()[2] || null,
          options: variant?.options || this.getSelectedOptionValues(),
          available: overrides.available ?? variant?.available ?? !submitButton?.hasAttribute('disabled'),
          featuredMediaId:
            overrides.featuredMediaId ||
            variant?.featured_media?.id ||
            featuredMediaId ||
            null,
          requestToken: overrides.requestToken ?? this.variantRequestToken,
          transactionId: overrides.transactionId || null,
          origin: overrides.origin || 'initial',
          url: `${window.location.pathname}${window.location.search}`,
          priceHtml: price?.innerHTML ?? null,
          submitDisabled: submitButton?.hasAttribute('disabled') ?? true,
          submitText: this.productForm?.submitButtonText?.textContent || '',
        };
      }

      async restoreLastCommittedVariantState() {
        const state = this.lastCommittedVariantState;
        if (!state) return { status: 'missing-state' };

        let mediaResult = null;
        if (state.featuredMediaId) {
          mediaResult = this.querySelector('media-gallery')?.navigateToMedia?.(
            `${this.dataset.section}-${state.featuredMediaId}`,
            {
              origin: 'variant',
              syncVariant: false,
              source: 'restore-variant',
              transactionId: state.transactionId,
              requestToken: state.requestToken,
              variantId: state.variantId,
              latestApprovedVariant: true,
            }
          );
          if (mediaResult?.status === 'pending' && mediaResult.settled) {
            mediaResult = await mediaResult.settled;
          }
          if (!mediaResult || !['applied', 'already-active'].includes(mediaResult.status)) {
            this.dataset.kkVariantMediaStatus = mediaResult?.status || 'missing-media';
            return { status: 'media-error', mediaResult };
          }
        }

        this.syncOptionControls(state.options || []);
        const selectedVariantData = this.variantSelectors?.querySelector('[data-selected-variant]');
        if (selectedVariantData) {
          const currentVariant = this.getSelectedVariant(this);
          selectedVariantData.textContent = JSON.stringify({
            ...(currentVariant || {}),
            id: Number(state.variantId),
            options: state.options || [],
            available: state.available,
            featured_media: state.featuredMediaId ? { id: Number(state.featuredMediaId) } : null,
          });
        }
        this.updateVariantInputs(state.variantId);

        const price = this.querySelector(`#price-${this.dataset.section}`);
        if (price && state.priceHtml !== null) price.innerHTML = state.priceHtml;
        this.productForm?.toggleSubmitButton(state.submitDisabled, state.submitText);

        if (this.dataset.updateUrl !== 'false' && state.url) window.history.replaceState({}, '', state.url);
        this.dataset.kkLastCommittedVariantId = state.variantId || '';
        this.dispatchEvent(
          new CustomEvent('kk:variant-selection-restored', {
            bubbles: true,
            detail: state,
          })
        );
        return { status: 'restored', mediaResult };
      }

      async updateMedia(html, variantFeaturedMediaId, context = {}) {
        if (!variantFeaturedMediaId) {
          return {
            status: 'missing-media',
            mediaId: null,
            transactionId: context.transactionId || null,
            requestToken: context.requestToken ?? null,
          };
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) {
          return {
            status: 'missing-media',
            mediaId: String(variantFeaturedMediaId),
            transactionId: context.transactionId || null,
            requestToken: context.requestToken ?? null,
          };
        }
        const preserveMediaOrder = mediaGallery?.kkPreserveMediaOrder === true;
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (!preserveMediaOrder && mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        let mediaResult = mediaGallery.navigateToMedia(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          {
            ...context,
            prepend: !preserveMediaOrder,
          }
        );
        if (mediaResult?.status === 'pending' && mediaResult.settled) {
          mediaResult = await mediaResult.settled;
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        return mediaResult;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
